import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import Heading from '@bibliocommons/heading';
import { ExpandableHtml } from '@bibliocommons/expandable';
import { bibShape } from '@bibliocommons/bc-prop-types';

import Field from 'app/components/bibs/shared/MetadataDisplay/Field';
import { fieldShape } from 'app/shapes/bibPage';

import './MediaSummary.scss';

export default function MediaSummary({
  bib,
  mediaSummary,
  audienceInfo,
  contributorPerformers,
  contributorCredits,
  eventInfo,
  languageInfo
}) {
  const isMovieTvVideo = bib.getIn(['briefInfo', 'superFormats'])?.includes('MOVIES_TV_VIDEO');
  const isMusicSound = bib.getIn(['briefInfo', 'superFormats'])?.includes('MUSIC_SOUND');
  const hasMovieTvInfo = isMovieTvVideo && (!!audienceInfo?.size || !!languageInfo?.size);
  const hasMusicSoundInfo =
    isMusicSound && (!!eventInfo?.size || !!contributorPerformers?.size || !!contributorCredits?.size);
  const hasAdditionalInfo = hasMovieTvInfo || hasMusicSoundInfo;

  const renderField = field => field?.size > 0 && <Field key={field.get('fieldName')} field={field} compact />;

  function renderAdditionalInfo() {
    if (!hasAdditionalInfo) return null;

    return (
      <>
        {hasMovieTvInfo && (
          <div className="language-and-audience-info">
            {renderField(audienceInfo)}
            {renderField(languageInfo)}
          </div>
        )}
        {hasMusicSoundInfo && (
          <div className="event-and-contributor-info">
            {renderField(eventInfo)}
            {renderField(contributorPerformers)}
            {renderField(contributorCredits)}
          </div>
        )}
      </>
    );
  }

  if (!mediaSummary && !hasAdditionalInfo) return null;

  return (
    <div className="cp-media-summary">
      <Heading tagName="h3" size="medium" variant>
        <FormattedMessage id="media_summary" />
      </Heading>
      {mediaSummary ? (
        <ExpandableHtml
          htmlText={mediaSummary}
          expandLinkHref="#media-summary-read-more"
          dataKey="media-summary-read-more-link"
          textKey="media_summary"
        />
      ) : null}
      {renderAdditionalInfo()}
    </div>
  );
}

MediaSummary.propTypes = {
  bib: bibShape.isRequired,
  mediaSummary: PropTypes.string,
  audienceInfo: fieldShape,
  contributorPerformers: fieldShape,
  contributorCredits: fieldShape,
  eventInfo: fieldShape,
  languageInfo: fieldShape
};
